import { DesktopNavButton } from '@crowdex/ui/components/desktop';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { ExternalLink, FileImage, Link2 } from 'lucide-react';
import { useState } from 'react';
import { SummarySection } from '@/components/desktop-summary/summary-primitives';

/** Sources shown as chips before "+N" opens them all. */
const CHIP_COUNT = 2;

type Source = { label: string; url: string };

const isFile = (url: string) =>
  url.startsWith('file:') || url.startsWith('data:') || url.startsWith('blob:');

/** A source's chip: a link by its site ("code.claude.com"), a file by its name. */
export function sourceChipLabel(source: Source): string {
  if (isFile(source.url)) return source.label;
  try {
    return new URL(source.url).hostname.replace(/^www\./, '') || source.label;
  } catch {
    return source.label;
  }
}

const open = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * The Summary's Sources (D5, mobile 36): the first ones as chips, then "+N" for the list of all
 * the files and links attached to the conversation.
 */
export function SummarySourcesSection({ sources }: { sources: readonly Source[] }) {
  const [listOpen, setListOpen] = useState(false);
  const chips = sources.slice(0, CHIP_COUNT);
  const more = sources.length - chips.length;

  return (
    <SummarySection count={sources.length} title="Sources">
      {sources.length === 0 ? (
        <p className="type-meta text-muted-foreground">
          Attach files from the composer to add sources.
        </p>
      ) : (
        <div className="flex min-w-0 flex-wrap gap-1.5 pt-0.5">
          {chips.map((source) => (
            <button
              className="type-meta h-7 min-w-0 max-w-full truncate rounded-control bg-control px-2.5 text-foreground/85 transition-colors hover:bg-accent hover:text-foreground"
              key={source.url}
              onClick={() => open(source.url)}
              title={source.label}
              type="button"
            >
              {sourceChipLabel(source)}
            </button>
          ))}
          {more > 0 && (
            <button
              aria-label={`All ${sources.length} sources`}
              className="type-meta h-7 shrink-0 rounded-control px-2 text-muted-foreground tabular-nums transition-colors hover:bg-accent hover:text-foreground"
              onClick={() => setListOpen(true)}
              type="button"
            >
              +{more}
            </button>
          )}
        </div>
      )}

      <Dialog onOpenChange={setListOpen} open={listOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sources</DialogTitle>
            <DialogDescription>Files and links attached to this conversation.</DialogDescription>
          </DialogHeader>
          <div className="grid max-h-96 overflow-y-auto">
            {sources.map((source) => {
              const Icon = isFile(source.url) ? FileImage : Link2;
              return (
                <DesktopNavButton
                  density="compact"
                  key={source.url}
                  onClick={() => open(source.url)}
                >
                  <Icon className="size-4" />
                  <span className="min-w-0 flex-1 truncate">{source.label}</span>
                  <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
                </DesktopNavButton>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </SummarySection>
  );
}
